/* screens/dev/UiKitParts3.jsx — витрина (часть 3): DataTable, карточки-плитки,
   виджет аналитики, оверлеи, состояния экранов. */

import React from 'react';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { DocumentTile, IngredientGridCard, MenuGridCard, MiniBarChart, RecipeCard, SkeletonGrid, WidgetCard } from '../../components/patterns/Cards.jsx';
import { DonutChart, LineChartMulti, StackedBarChart } from '../../components/patterns/charts/Charts.jsx';
import { ExpandableList } from '../../components/patterns/ExpandableList.jsx';
import { DataTable, SortMenu } from '../../components/patterns/DataTable.jsx';
import { EntityPickerModal, FilterDrawer, LanguagePicker } from '../../components/patterns/Overlays.jsx';
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from '../../components/patterns/States.jsx';
import { FoodCostBadge, StatusBadge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Kebab } from '../../components/ui/Buttons.jsx';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { Checkbox, Field, Segmented, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { PopoverItem } from '../../components/ui/Popover.jsx';
import { fmt } from '../../lib/format.js';
import { ColHead, useReportTable } from '../analytics/reportTable.jsx';
import { ResponsiveTable } from '../analytics/ResponsiveTable.jsx';
import { TableToolbar, useTableSettings } from '../analytics/tableSettings.jsx';
import { TableSearch, useTableSearch } from '../analytics/TableSearch.jsx';
import { TableCols } from '../analytics/ColumnWidths.jsx';
import { NameCell } from '../analytics/NameCell.jsx';
import { HiddenItemsButton } from '../analytics/HiddenItemsButton.jsx';
import { UiKitSection as Section } from './UiKitParts1.jsx';

  const { useState, useMemo } = React;

  /* ── фикстуры ── */
  const ROWS = [
    { id: 1, name: 'Борщ московский', kind: 'Блюдо', price: 142, status: 'ready' },
    { id: 2, name: 'Бульон говяжий', kind: 'Полуфабрикат', price: 64, status: 'ready' },
    { id: 3, name: 'Сметана 20%', kind: 'Ингредиент', price: 38, status: 'unbound' },
    { id: 4, name: 'Цезарь с курицей', kind: 'Блюдо', price: 198, status: 'draft' },
    { id: 5, name: 'Соус песто', kind: 'Полуфабрикат', price: 88, status: 'stub' },
    { id: 6, name: 'Том ям', kind: 'Блюдо', price: 286, status: 'failed' },
    { id: 7, name: 'Лосось с/с', kind: 'Ингредиент', price: 920, status: 'ready' },
    { id: 8, name: 'Тирамису', kind: 'Блюдо', price: 176, status: 'ready' },
  ];
  const COLUMNS = [
    { key: 'name', label: 'Название', mono: false },
    { key: 'kind', label: 'Тип', mono: false },
    { key: 'price', label: 'Цена', align: 'right', render: (r) => fmt.money(r.price) },
    { key: 'status', label: 'Статус', mono: false, render: (r) => <StatusBadge status={r.status} size="sm" /> },
    { key: 'act', label: '', mono: false, render: () => <Kebab>{({ close }) => <div className="stack stack-2"><PopoverItem icon="edit" close={close}>Открыть</PopoverItem><PopoverItem icon="trash" danger close={close}>Удалить</PopoverItem></div>}</Kebab> },
  ];
  const INGREDIENTS = [
    { name: 'Лосось слабосолёный', amount_rub: 920, unit_label: 'кг', supplier_name: 'РыбаОпт' },
    { name: 'Сметана 20%', amount_rub: 38, unit_label: 'шт', supplier_name: 'Молочный двор' },
    { name: 'Томаты черри', amount_rub: 240, unit_label: 'кг', supplier_name: 'ОвощБаза' },
    { name: 'Оливковое масло E.V.', amount_rub: 680, unit_label: 'л', supplier_name: 'ИталияФуд' },
    { name: 'Пармезан 32 мес.', amount_rub: 1840, unit_label: 'кг', supplier_name: 'СырХаус' },
  ];
  const RECIPES = [
    { name: 'Борщ московский', kind: 'dish', status: 'ready', kcal: 320, yield_g: 350, cost_rub: 142, allergens: ['Глютен', 'Молоко', 'Сельдерей'] },
    { name: 'Бульон говяжий', kind: 'semi', status: 'ready', kcal: 48, yield_g: 1000, cost_rub: 64, allergens: ['Сельдерей'] },
    { name: 'Соус песто', kind: 'semi', status: 'unbound', kcal: 420, yield_g: 200, cost_rub: 88, allergens: ['Орехи', 'Молоко'] },
    { name: 'Цезарь с курицей', kind: 'dish', status: 'draft', kcal: 540, yield_g: 280, cost_rub: 198, allergens: ['Глютен', 'Яйца', 'Рыба', 'Молоко'] },
  ];
  const MENUS = [
    { name: 'Зимнее меню 2026', status: 'ready', dishes: 42, avg_check: 1280, avg_cost: 384, fc: 27 },
    { name: 'Бизнес-ланч', status: 'draft', dishes: 12, avg_check: 540, avg_cost: 232, fc: 43 },
    { name: 'Банкетное', status: 'stub', dishes: 28, avg_check: 2400, avg_cost: 720, fc: 35 },
  ];
  const DOCS = [
    { name: 'Технологическая карта.pdf', meta: 'PDF · 2,4 МБ · 28 мая', filetype: 'pdf', summary: 'Документ описывает технологию приготовления и нормы закладки для горячего цеха.', summary_status: 'ready' },
    { name: 'Накладная №4821.jpg', meta: 'JPG · 1,1 МБ · 27 мая', filetype: 'img', summary_status: 'processing' },
  ];
  const CHART = [{ label: 'Пн', value: 42 }, { label: 'Вт', value: 58 }, { label: 'Ср', value: 36 }, { label: 'Чт', value: 64 }, { label: 'Пт', value: 88 }];

  /* Фикстуры графиков с длинной легендой (детерминированные — витрина не должна
     менять картинку от перерисовки). Десять серий — кнопки «Скрыть все / Показать
     все» и вся палитра; четырнадцать — ещё и разворот легенды (больше 12 чипов). Малые средние
     («0,3» в час) — чтобы было видно fmt.avg на подсказке и оси. */
  const CATS = ['Горячее', 'Напитки', 'Бар', 'Десерты', 'Закуски', 'Салаты', 'Супы', 'Гарниры',
    'Завтраки', 'Пицца', 'Паста', 'Хлеб', 'Соусы', 'Детское меню'];
  const DEMO_HOURS = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
  const wave = (x, k) => Math.max(0, Math.round((Math.sin(x / 2.2 + k) + 1.15) * (9 - (k % 8)) * 10) / 100);
  // десять серий — вся палитра --c-series-1…10: соседние цвета стека должны читаться
  const DEMO_STACK = CATS.slice(0, 10).map((name, k) => ({
    id: 'k' + k, name, values: DEMO_HOURS.map((h) => wave(h - 9, k)),
  }));
  // 20 серий — свёртка подсказки: «Всего», 7 крупнейших часа и «ещё N категорий · сумма»
  const DEMO_STACK_MANY = Array.from({ length: 20 }, (_, k) => ({
    id: 'm' + k, name: CATS[k % CATS.length] + (k >= CATS.length ? ' ' + (k + 1) : ''),
    values: DEMO_HOURS.map((h) => wave(h - 9, k)),
  }));
  const DEMO_LINES = CATS.map((name, k) => ({
    id: 'l' + k, name,
    points: Array.from({ length: 14 }, (_, d) => ({
      date: '2026-09-' + String(d + 1).padStart(2, '0'), value: Math.round(wave(d, k) * 10000) / 10,
    })),
  }));
  const DEMO_DONUT = Array.from({ length: 30 }, (_, i) => ({
    label: i < CATS.length ? CATS[i] : 'Категория ' + (i + 1), value: Math.round(900000 / (i + 1.6)),
  }));
  const DEMO_RATING = CATS.concat(['Морепродукты']).map((name, i) => ({ name, value: 120 - i * 7 }));
  const catUnit = (n) => fmt.plural(n, 'категория', 'категории', 'категорий');
  // графикам нужна ширина, как в отчёте: в плитке на четверть экрана легенда не читается
  const DEMO_WIDE = {
    display: 'grid', gap: 'var(--space-12)',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
  };

  /* ── Таблица Аналитики: поиск, ширина колонок, «имя + действие», «Скрыто: N» ──
     Общий слой таблиц раздела (screens/analytics): TableSearch + useTableSearch,
     ширины (ResponsiveTable widths + TableCols, ручка в ColHead), NameCell,
     HiddenItemsButton. Данные выдуманные; одно название нарочно длинное — кнопка
     «Скрыть» обязана остаться в ячейке. */
  const AN_ROWS = [
    { id: 'r1', name: 'Борщ московский', group: 'Супы', qty: 412, revenue: 160680 },
    { id: 'r2', name: 'Стейк рибай из мраморной говядины с соусом демиглас, печёным картофелем и спаржей на гриле', group: 'Горячее', qty: 96, revenue: 162240 },
    { id: 'r3', name: 'Ёжики в томатном соусе', group: 'Горячее', qty: 188, revenue: 73320 },
    { id: 'r4', name: 'Цезарь с курицей', group: 'Салаты', qty: 301, revenue: 141470 },
    { id: 'r5', name: 'Свёкла печёная с козьим сыром', group: 'Салаты', qty: 77, revenue: 30030 },
    { id: 'r6', name: 'Тирамису', group: 'Десерты', qty: 254, revenue: 96520 },
    { id: 'r7', name: 'Соус сырный (модификатор)', group: 'Соусы', qty: 640, revenue: 25600 },
  ];
  const AN_COLS = [
    { key: 'name', label: 'Позиция', type: 'text', fixed: true, minWidth: 96, get: (r) => r.name },
    { key: 'group', label: 'Группа', type: 'enum', get: (r) => r.group },
    { key: 'qty', label: 'Кол-во', type: 'num', align: 'num', thClass: 'num', get: (r) => r.qty },
    { key: 'revenue', label: 'Выручка', type: 'num', align: 'num', thClass: 'num', get: (r) => r.revenue },
  ];
  function AnalyticsTableDemo() {
    const [hiddenIds, setHiddenIds] = useState(() => ['r7']);
    const search = useTableSearch();
    const settings = useTableSettings('uikit-demo', AN_COLS);
    const colState = useReportTable(settings.visible, AN_ROWS);
    const rows = useMemo(() => colState.apply(search.filter(
      AN_ROWS.filter((r) => !hiddenIds.includes(r.id)), (r) => r.name)),
    [colState.apply, search.filter, hiddenIds]);
    const hidden = AN_ROWS.filter((r) => hiddenIds.includes(r.id))
      .map((r) => ({ key: r.id, name: r.name, sub: r.group }));
    const cell = (c, r) => {
      if (c.key === 'name') {
        return (
          <td key={c.key} className="an-td-name" data-label={c.label}>
            <NameCell name={r.name} action={(
              <button type="button" className="abc-excl-btn" title="Скрыть из таблицы"
                aria-label={'Скрыть: ' + r.name} onClick={() => setHiddenIds((v) => [...v, r.id])}>
                <Icon name="eye-off" size={12} />
              </button>
            )} />
          </td>
        );
      }
      if (c.key === 'group') return <td key={c.key} data-label={c.label}>{r.group}</td>;
      if (c.key === 'qty') return <td key={c.key} className="num" data-label={c.label}>{r.qty}</td>;
      return <td key={c.key} className="num" data-label={c.label}>{fmt.money(r.revenue)}</td>;
    };
    return (
      <div className="an-layer stack stack-12">
        <div className="an-toolbar">
          <div className="an-toolbar__group">
            <TableSearch search={search} placeholder="Поиск позиции…" label="Поиск позиции" />
          </div>
        </div>
        <TableToolbar settings={settings} colState={colState} count={rows.length}
          countUnit={(n) => fmt.plural(n, 'позиция', 'позиции', 'позиций')}>
          <HiddenItemsButton items={hidden} label="Скрыто" title="Позиции, скрытые из таблицы витрины"
            restoreTitle="Вернуть" onRestore={(it) => setHiddenIds((v) => v.filter((x) => x !== it.key))}
            onRestoreAll={() => setHiddenIds([])} />
        </TableToolbar>
        <ResponsiveTable className="uikit-antable" widths={settings}>
          <table>
            <TableCols cols={settings.visible} />
            <thead>
              <tr>{settings.visible.map((c) => <ColHead key={c.key} col={c} state={colState} />)}</tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((r) => <tr key={r.id}>{settings.visible.map((c) => cell(c, r))}</tr>) : (
                <tr><td colSpan={settings.visible.length}>Ничего не найдено</td></tr>
              )}
            </tbody>
          </table>
        </ResponsiveTable>
        {/* Тот же список в режиме «только чтение» — участнику без права правки: видно,
            что скрыто, вернуть нельзя (кнопок нет, внизу — кто может). */}
        <div className="an-toolbar">
          <span className="uikit-state-label">Только чтение (нет права правки):</span>
          <HiddenItemsButton items={hidden} label="Скрыто" title="Позиции, скрытые из таблицы витрины"
            readOnly readOnlyNote="Вернуть может участник с правом правки." />
        </div>
      </div>
    );
  }

  /* ── DataTable ── */
  function DataTableSection() {
    const [mode, setMode] = useState('data');
    const rows = mode === 'empty' ? [] : ROWS;
    return (
      <Section id="table" title="DataTable — адаптивный примитив" sub="Один компонент: таблица на md+ (≤720px → карточки label:value, все данные видны). // заменяет: Table + кастомные grid-таблицы.">
        <div className="uikit-grid" style={{ marginBottom: 'var(--space-12)' }}>
          <Segmented value={mode} onChange={setMode} options={[{ value: 'data', label: 'Данные' }, { value: 'compact', label: 'Compact+zebra' }, { value: 'loading', label: 'Loading' }, { value: 'empty', label: 'Empty' }]} />
          <div className="grow" />
          <SortMenu value="name" onChange={() => {}} options={[{ value: 'name', label: 'По названию' }, { value: 'price', label: 'По цене' }]} />
        </div>
        <DataTable columns={COLUMNS} rows={rows} rowKey="id"
          compact={mode === 'compact'} zebra={mode === 'compact'} loading={mode === 'loading'}
          onRowClick={() => {}} empty={<EmptyState icon="kitchen" serif={false} title="Пусто" body="Ничего не найдено по фильтру." />} />
        <div style={{ marginTop: 'var(--space-16)' }}>
          <div className="uikit-state-label">Таблица Аналитики (общий слой screens/analytics): поиск TableSearch («ё» = «е», один фильтр в счётчике), ширина колонок — потяните границу заголовка, двойной клик — по содержимому, стрелки на ручке, «Столбцы → Сбросить»; ячейка «имя + действие» (NameCell) — кнопка «Скрыть» при любой длине названия; «Скрыто: N» (HiddenItemsButton; ниже — он же в режиме «только чтение»). Уже 720px — карточки, ручек нет.</div>
          <AnalyticsTableDemo />
        </div>
      </Section>
    );
  }

  /* ── Карточки-плитки ── */
  function CardsSection() {
    const [skel, setSkel] = useState(false);
    return (
      <Section id="cards" title="Карточки-плитки каталогов" sub="Сетка var(--grid-cols): 1 (xs) → 5 (4k). IngredientGridCard, RecipeCard, MenuGridCard, DocumentCard, SkeletonGrid.">
        <div className="stack stack-16">
          <div><div className="uikit-state-label row between"><span>Сырьё</span><button className="btn btn--ghost btn--sm" onClick={() => setSkel((s) => !s)}>{skel ? 'Показать данные' : 'Показать skeleton'}</button></div>
            {skel ? <SkeletonGrid count={5} /> : <div className="tile-grid">{INGREDIENTS.map((it, i) => <IngredientGridCard key={i} item={it} onClick={() => {}} menu={<Kebab>{({ close }) => <div className="stack stack-2"><PopoverItem icon="edit" close={close}>Изменить</PopoverItem></div>}</Kebab>} />)}</div>}
          </div>
          <div><div className="uikit-state-label">ТТК / полуфабрикаты</div><div className="tile-grid">{RECIPES.map((it, i) => <RecipeCard key={i} item={it} onClick={() => {}} />)}</div></div>
          <div><div className="uikit-state-label">Меню</div><div className="tile-grid">{MENUS.map((it, i) => <MenuGridCard key={i} item={it} onClick={() => {}} />)}</div></div>
          <div><div className="uikit-state-label">Документы базы знаний</div><div className="tile-grid">{DOCS.map((it, i) => <DocumentTile key={i} item={it} menu={<Kebab>{({ close }) => <div className="stack stack-2"><PopoverItem icon="download" close={close}>Скачать</PopoverItem><PopoverItem icon="trash" danger close={close}>Удалить</PopoverItem></div>}</Kebab>} />)}</div></div>
        </div>
      </Section>
    );
  }

  /* ── Виджет аналитики ── */
  function WidgetSection() {
    const [state, setState] = useState('content');
    return (
      <Section id="widget" title="WidgetCard — каркас виджета аналитики" sub="Состояния loading / error / empty / content. График — лёгкий SVG/CSS bar-chart (эквивалент recharts в прототипе). Ниже — легенды графиков (ChartLegend) и свёрнутые списки (ExpandableList) Аналитики.">
        <div className="uikit-grid" style={{ marginBottom: 'var(--space-12)' }}>
          <Segmented value={state} onChange={setState} options={[{ value: 'content', label: 'Content' }, { value: 'loading', label: 'Loading' }, { value: 'empty', label: 'Empty' }, { value: 'error', label: 'Error' }]} />
        </div>
        <div className="tile-grid" style={{ '--grid-cols': 'min(2, var(--grid-cols))' }}>
          <WidgetCard title="Продажи по дням" subtitle="За текущую неделю" onExport={() => {}}
            loading={state === 'loading'} empty={state === 'empty'} error={state === 'error'} onRetry={() => setState('content')}>
            <MiniBarChart data={CHART} />
          </WidgetCard>
          <WidgetCard title="Топ-5 по марже" subtitle="₽ за порцию" onExport={() => {}}>
            <MiniBarChart data={[{ label: 'Том ям', value: 186 }, { label: 'Цезарь', value: 142 }, { label: 'Борщ', value: 98 }, { label: 'Песто', value: 76 }, { label: 'Тирамису', value: 64 }]} color="var(--c-teal-600)" />
          </WidgetCard>
        </div>

        <div className="stack stack-16" style={{ marginTop: 'var(--space-16)' }}>
          <div>
            <div className="uikit-state-label">ChartLegend — легенда StackedBarChart / LineChartMulti: чип = переключатель (aria-pressed), «Скрыть все / Показать все» от 4 серий, всё скрыто — пустое состояние с «Показать все»; больше 12 серий — «Вся легенда — N»</div>
            <div style={DEMO_WIDE}>
              <div className="uikit-panel">
                <div className="uikit-state-label">10 серий — десять разных цветов палитры, средние на час (fmt.avg / fmt.avgAxis)</div>
                <StackedBarChart hours={DEMO_HOURS} series={DEMO_STACK} valueFormat={fmt.avg} axisFormat={fmt.avgAxis} />
              </div>
              <div className="uikit-panel">
                <div className="uikit-state-label">StackTip — подсказка при 20 сериях: «Всего», 7 крупнейших часа, «ещё N категорий · сумма»; наведённая серия всегда в списке</div>
                <StackedBarChart hours={DEMO_HOURS} series={DEMO_STACK_MANY} valueFormat={fmt.avg} axisFormat={fmt.avgAxis} />
              </div>
              <div className="uikit-panel">
                <div className="uikit-state-label">14 серий — легенда свёрнута до 10</div>
                <LineChartMulti series={DEMO_LINES} valueFormat={fmt.money} axisFormat={fmt.moneyAxis} />
              </div>
            </div>
          </div>
          <div>
            <div className="uikit-state-label">ExpandableList — «первые 10 + показать все» (больше двух спрятанных строк): легенда доната с итогом «Ещё N · сумма · доля» и обычный рейтинг</div>
            <div style={DEMO_WIDE}>
              <div className="uikit-panel">
                <DonutChart data={DEMO_DONUT} valueFormat={fmt.money} legendUnit={catUnit} />
              </div>
              <div className="uikit-panel">
                <ExpandableList items={DEMO_RATING} as="ol" listClassName="stack stack-2" unit={catUnit}
                  render={(r) => (
                    <li key={r.name} className="row between t-body-sm">
                      <span>{r.name}</span><span className="num">{r.value}</span>
                    </li>
                  )} />
              </div>
            </div>
          </div>
        </div>
      </Section>
    );
  }

  /* ── Оверлеи ── */
  function OverlaysSection() {
    const [modalSize, setModalSize] = useState(null);
    const [drawer, setDrawer] = useState(false);
    const [drawer2, setDrawer2] = useState(false);
    const [drawer3, setDrawer3] = useState(false);
    const [drawerXl, setDrawerXl] = useState(false);
    const [filter, setFilter] = useState(false);
    const [picker, setPicker] = useState(false);
    const [lang, setLang] = useState(false);
    const toast = useToast();
    const confirm = useConfirm();
    const PICK_ITEMS = INGREDIENTS.map((it, i) => ({ id: i, name: it.name, hint: it.supplier_name }));
    return (
      <Section id="overlays" title="Оверлеи" sub="Единый Drawer (стек 1–3, размеры sm/md/lg/xl/full, фильтр-режим), Modal (xs–xl, bottom-sheet на xs), EntityPickerModal, ConfirmDialog (// заменяет window.confirm), Toast.">
        <div className="stack stack-12">
          <div className="uikit-grid">
            {['xs', 'sm', 'md', 'lg', 'xl'].map((s) => <Button key={s} variant="secondary" size="sm" onClick={() => setModalSize(s)}>Modal {s}</Button>)}
          </div>
          <div className="uikit-grid">
            <Button variant="secondary" onClick={() => setDrawer(true)}>Drawer-стек</Button>
            <Button variant="secondary" onClick={() => setDrawerXl(true)}>Drawer xl</Button>
            <Button variant="secondary" onClick={() => setFilter(true)}>FilterDrawer</Button>
            <Button variant="secondary" onClick={() => setPicker(true)}>EntityPicker</Button>
            <Button variant="secondary" onClick={() => setLang(true)}>LanguagePicker</Button>
            <Button variant="secondary" onClick={async () => { const ok = await confirm({ title: 'Удалить ТТК «Борщ московский»?', body: 'Действие необратимо. Карточка и её версии будут удалены.', confirmLabel: 'Удалить', tone: 'danger' }); toast.show(ok ? 'Удалено' : 'Отменено', ok ? 'success' : 'muted'); }}>Confirm (danger)</Button>
          </div>
          <div className="uikit-grid">
            <Button size="sm" variant="ghost" onClick={() => toast.show('Сохранено', 'success')}>Toast success</Button>
            <Button size="sm" variant="ghost" onClick={() => toast.show('3 ТТК без цены', 'warning')}>Toast warning</Button>
            <Button size="sm" variant="ghost" onClick={() => toast.show('Не удалось сохранить', 'danger')}>Toast danger</Button>
            <Button size="sm" variant="ghost" onClick={() => toast.show('Отчёт готов', 'accent')}>Toast accent</Button>
          </div>
        </div>

        <Modal open={!!modalSize} onClose={() => setModalSize(null)} title={`Модальное окно · ${modalSize || ''}`} size={modalSize || 'md'}
          footer={<><Button variant="ghost" onClick={() => setModalSize(null)}>Отмена</Button><Button variant="primary" onClick={() => { setModalSize(null); toast.show('Сохранено', 'success'); }}>Сохранить</Button></>}>
          <div className="stack stack-12"><p className="t-body text-dim">focus-trap, scroll-lock, ESC, клик по подложке. На xs — bottom-sheet.</p><Field label="Название"><TextInput defaultValue="Борщ московский" /></Field></div>
        </Modal>

        <Drawer open={drawer} onClose={() => setDrawer(false)} title="ТТК · Борщ московский" size="md" breadcrumbs="Кухня · ТТК"
          footer={<><Button variant="ghost" onClick={() => setDrawer(false)}>Закрыть</Button><Button variant="primary" onClick={() => setDrawer2(true)}>Открыть строку состава →</Button></>}>
          <div className="stack stack-12"><p className="t-body text-dim">Правый drawer, стек до 3 уровней, крошки, кнопка «назад».</p><div className="cluster cluster-12"><FoodCostBadge value={27} /><StatusBadge status="ready" /></div></div>
        </Drawer>
        <Drawer open={drawer2} onClose={() => setDrawer2(false)} title="Строка состава · Бульон" size="sm" level={2} showBack onBack={() => setDrawer2(false)} breadcrumbs="Кухня · ТТК · Состав"
          footer={<Button variant="primary" onClick={() => setDrawer3(true)}>Глубже →</Button>}>
          <p className="t-body text-dim">Уровень 2. Подложку затемняет только level 1.</p>
        </Drawer>
        <Drawer open={drawer3} onClose={() => setDrawer3(false)} title="Карточка сырья" size="sm" level={3} showBack onBack={() => setDrawer3(false)} breadcrumbs="… · Сырьё">
          <p className="t-body text-dim">Уровень 3 — предел стека.</p>
        </Drawer>
        {/* xl — панель-отчёт на всю таблицу (Аналитика → «Столы»): 1180px, на ≤720px —
            во весь экран, как остальные размеры. */}
        <Drawer open={drawerXl} onClose={() => setDrawerXl(false)} title="Столы" size="xl"
          breadcrumbs="Обзор · Производительность">
          <div className="an-layer stack stack-12">
            <p className="t-body text-dim">Drawer xl — 1180px (lg — 820px). Для панелей, где тепловая карта и таблица на полтора десятка столбцов не помещаются в lg; на телефоне — во весь экран.</p>
            <AnalyticsTableDemo />
          </div>
        </Drawer>

        <FilterDrawer open={filter} onClose={() => setFilter(false)} onReset={() => {}} onApply={() => toast.show('Фильтр применён', 'accent')} activeCount={2}>
          <Field label="Статус"><div className="stack stack-6"><Checkbox label="Готово" defaultChecked /><Checkbox label="Без цены" /><Checkbox label="Черновик" /></div></Field>
          <Field label="Тип карты"><div className="stack stack-6"><Checkbox label="Блюдо" defaultChecked /><Checkbox label="Полуфабрикат" /></div></Field>
        </FilterDrawer>

        <EntityPickerModal open={picker} onClose={() => setPicker(false)} title="Добавить сырьё" placeholder="Найти ингредиент…" items={PICK_ITEMS} multi onSubmit={(p) => toast.show(`Добавлено: ${p.length}`, 'success')} />
        <LanguagePicker open={lang} onClose={() => setLang(false)} value="ru" onChange={() => {}} />
      </Section>
    );
  }

  /* ── Состояния экранов ── */
  function StatesSection() {
    const toast = useToast();
    return (
      <Section id="states" title="Состояния экранов" sub="Каждый экран обязан иметь loading (skeleton) / empty / error / 403. Единые компоненты States.">
        <div className="uikit-states">
          <div className="set-card"><LoadingState /></div>
          <div className="set-card"><EmptyState icon="kitchen" title="Здесь пока пусто" body="Создайте первую карточку, чтобы начать." action={<Button variant="primary" iconLeft="plus">Создать</Button>} /></div>
          <div className="set-card"><ErrorState onRetry={() => toast.show('Повтор запроса…', 'muted')} /></div>
          <div className="set-card"><ForbiddenState /></div>
        </div>
      </Section>
    );
  }

export { DataTableSection };
export { CardsSection };
export { WidgetSection };
export { OverlaysSection };
export { StatesSection };
